import { memo, useCallback, FC } from 'react';
import { useTranslator } from '@picsart/rc/services/localization';
import Card from '@picsart/rc/editor/Card';
import Grid from '@picsart/rc/editor/Grid';
import Sidebar, { NewSidebarBody } from '@picsart/rc/editor/Sidebar';
import SocialImage from '@picsart/rc/editor/SocialImage';
import { useSelector, useDispatch } from 'react-redux';
import { setRatioFormat, getRatioFormat } from 'modules/ratioFormat';
import { ratioFormats } from 'constants/ratioFormats';
import { RatioFormatObject, RatioFormatType } from 'types/RatioFormat';
import analytics from 'utils/analytics';
import { addURLParameters } from 'utils/directLinks';
import { getCurrentSceneId, getScenes } from 'modules/scenes';
import { setRatioChanged } from 'modules/canvas';
import { usePlayer } from 'utils/PlayerContext';

export type FormatCardProps = RatioFormatObject & {
  selected?: boolean;
  selectHandler: (type: RatioFormatType) => void;
};

export const RatioFormatCard: FC<FormatCardProps> = memo(
  ({ ratioFormatType, icon, selectHandler, titleText, ratioText, selected }) => (
    <Card onClick={() => selectHandler(ratioFormatType)} isActive={selected}>
      <div data-test={`ratio-format_select-${ratioFormatType}`}>
        <SocialImage iconName={icon} title={titleText} subTitle={ratioText} />
      </div>
    </Card>
  )
);

type FormatToolBaseProps = {
  selectedType: RatioFormatType;
  selectHandler: (type: RatioFormatType) => void;
};

export const RatioFormatBase: FC<FormatToolBaseProps> = ({ selectedType, selectHandler }) => {
  const translator = useTranslator();
  return (
    <div data-test="tool-panel_fit" sx={{ height: '100%', py: '10px' }}>
      <Sidebar>
        <NewSidebarBody isScrollable>
          <Grid>
            {Object.values(ratioFormats).map((props, i) => (
              <RatioFormatCard
                {...props}
                key={i}
                selectHandler={selectHandler}
                selected={selectedType === props.ratioFormatType}
                titleText={translator(props.titleText)}
              />
            ))}
          </Grid>
        </NewSidebarBody>
      </Sidebar>
    </div>
  );
};

export const RatioFormat = () => {
  const selectedType = useSelector(getRatioFormat);
  const currentSceneId = useSelector(getCurrentSceneId);
  const scenes = useSelector(getScenes);
  const { pause } = usePlayer();

  const dispatch = useDispatch();

  const selectHandler = useCallback(
    (ratioFormat: RatioFormatType) => {
      analytics.fitAction(ratioFormats[ratioFormat].ratioText);
      addURLParameters({ fit: ratioFormat });
      pause();
      dispatch(setRatioFormat(ratioFormat));
      dispatch(setRatioChanged(selectedType));
    },
    [selectedType, scenes, currentSceneId, pause]
  );

  return <RatioFormatBase selectedType={selectedType} selectHandler={selectHandler} />;
};
